/**
 * appOrigin — resolves the canonical origin URL for the current deployment.
 *
 * Priority:
 *   1. VITE_APP_URL (Netlify env var, set per context —
 *      production, branch:staging, branch-deploy:dev). This is the
 *      AUTHORITATIVE value when the app is deployed.
 *   2. window.location.origin (browser-local fallback).
 *
 * Why not just use `window.location.origin`?
 *
 * If the user signs up from the deployed dev URL, `window.location.origin`
 * is correctly `https://dev--betterclose.netlify.app` — but when Supabase
 * Auth generates the confirmation email link, it needs the redirect target
 * to be WHITELISTED in the Supabase project's `additional_redirect_urls`.
 * If it isn't whitelisted, Supabase silently falls back to `site_url`,
 * which (on fresh projects) defaults to `http://localhost:5173` — and the
 * user clicks the email link on their phone or another device and gets
 * ERR_CONNECTION_REFUSED. Threading the same URL through both Supabase Auth
 * config AND the front-end (VITE_APP_URL) makes the behavior deterministic.
 *
 * Usage:
 *   emailRedirectTo: `${appOrigin()}/email-confirmed`
 */
export function appOrigin(): string {
  const envUrl = (import.meta.env.VITE_APP_URL as string | undefined)?.trim()
  if (envUrl && envUrl.length > 0) return envUrl.replace(/\/$/, '')
  // Fallback for local dev + tests where VITE_APP_URL isn't injected.
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin
  }
  // Last-resort (SSR-like contexts that should never actually hit this path).
  return 'http://localhost:5173'
}
